import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink, Quote } from 'lucide-react';

/**
 * GoogleReviewsSection — fetches and renders REAL Google reviews for the
 * business via our backend ``/api/google-reviews`` endpoint, which proxies
 * Google Places API (New) and caches in MongoDB for 6 hours.
 *
 * Mirrors the visual language of TestimonialsSection so both sections feel
 * native together. Adds a "Verified Google review" badge per Google branding.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tiny Google "G" logo as inline SVG — saves a network call & is exact brand color.
const GoogleLogo = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 48 48" aria-hidden className={className}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.7 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.4 36 44 30.4 44 24c0-1.2-.1-2.4-.4-3.5z" />
  </svg>
);

const Stars = ({ rating = 5 }) => (
  <div className="flex gap-0.5" data-testid="review-stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}`}
      />
    ))}
  </div>
);

const Avatar = ({ url, name }) => {
  const [errored, setErrored] = useState(false);
  if (url && !errored) {
    return (
      <img
        src={url}
        alt={name || 'Reviewer'}
        onError={() => setErrored(true)}
        loading="lazy"
        className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow"
      />
    );
  }
  const initial = (name || 'G').trim().charAt(0).toUpperCase();
  return (
    <div className="w-11 h-11 rounded-full grid place-items-center text-white font-bold text-base shadow ring-2 ring-white bg-gradient-to-br from-indigo-500 to-violet-600">
      {initial}
    </div>
  );
};

const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const cleanText = (review.text || '').replace(/^"|"$/g, '').trim();
  const isLong = cleanText.length > 280;
  const visible = expanded || !isLong ? cleanText : cleanText.slice(0, 280).trimEnd() + '…';

  return (
    <motion.figure
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.4 }}
      data-testid="google-review-card"
      className="relative bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-7 shadow-[0_2px_30px_-12px_rgba(15,23,42,0.12)] hover:shadow-[0_8px_40px_-12px_rgba(15,23,42,0.18)] transition-shadow flex flex-col h-full"
    >
      {/* Top accent line — Google brand gradient */}
      <div
        aria-hidden
        className="absolute inset-x-6 top-0 h-[3px] rounded-b-full bg-gradient-to-r from-[#4285F4] via-[#34A853] via-50% to-[#EA4335]"
      />

      {/* Header row: Verified badge + Stars */}
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[11px] font-semibold text-slate-700">
          <GoogleLogo className="w-3.5 h-3.5" />
          Verified Google review
        </span>
        <Stars rating={review.rating} />
      </div>

      {/* Quote icon */}
      <Quote className="w-7 h-7 text-indigo-100 mb-3" />

      {/* Body */}
      <blockquote className="text-sm sm:text-[15px] leading-relaxed text-slate-700 flex-1 whitespace-pre-line">
        {visible}
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium"
            data-testid="review-expand-btn"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </blockquote>

      {/* Attribution */}
      <figcaption className="flex items-center gap-3 pt-5 mt-5 border-t border-slate-100">
        <Avatar url={review.profile_photo_url} name={review.author_name} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">
            {review.author_name}
          </p>
          <p className="text-xs text-slate-500">
            {review.relative_time_description || 'Recent review'}
          </p>
        </div>
        {review.review_url && (
          <a
            href={review.review_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-indigo-600 transition-colors"
            aria-label="Open review on Google"
            data-testid="review-open-link"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </figcaption>
    </motion.figure>
  );
};

export const GoogleReviewsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/google-reviews`);
        if (!r.ok) throw new Error('bad status');
        const json = await r.json();
        if (alive) setData(json);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Gracefully hide the entire section if Google is unreachable AND there's
  // nothing useful to show — never leave a broken card on the landing page.
  if (error || (!loading && (!data || !data.reviews || data.reviews.length === 0))) {
    return null;
  }

  return (
    <section
      className="py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50/70 relative overflow-hidden"
      data-testid="google-reviews-section"
    >
      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-3xl mx-auto mb-10 lg:mb-14"
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 mb-3">
            <GoogleLogo className="w-4 h-4" />
            Verified on Google
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Real reviews.{' '}
            <span className="bg-gradient-to-r from-[#4285F4] via-[#34A853] to-[#EA4335] bg-clip-text text-transparent">
              Real people.
            </span>
          </h2>

          {/* Aggregate rating row */}
          {data && (
            <div className="mt-5 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm">
              <Stars rating={data.rating} />
              <span className="text-sm font-bold text-slate-900">
                {Number(data.rating || 0).toFixed(1)}
              </span>
              <span className="text-xs text-slate-500">
                · {data.total_review_count} Google review{data.total_review_count === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </motion.div>

        {/* Reviews grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="google-reviews-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-64 rounded-2xl bg-white border border-slate-200 animate-pulse" />
            ))}
          </div>
        ) : (
          <div
            className={`grid gap-6 ${
              (data?.reviews?.length || 0) >= 3
                ? 'sm:grid-cols-2 lg:grid-cols-3'
                : (data?.reviews?.length || 0) === 2
                ? 'sm:grid-cols-2 max-w-4xl mx-auto'
                : 'max-w-2xl mx-auto'
            }`}
          >
            {(data?.reviews || []).map((review, i) => (
              <ReviewCard key={i} review={review} />
            ))}
          </div>
        )}

        {/* CTA — view all on Google */}
        {data?.google_maps_uri && (
          <div className="text-center mt-10">
            <a
              href={data.google_maps_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md text-sm font-semibold text-slate-700 transition"
              data-testid="view-all-google-btn"
            >
              <GoogleLogo className="w-4 h-4" />
              See all reviews on Google
              <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
            </a>
            <p className="mt-3 text-[11px] text-slate-400">
              Reviews shown are real and pulled from Google Maps. Refreshed every few hours.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
